import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Link as LinkIcon,
  Upload,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const VERDICT_COLOR = {
  compliant:         { ring: 'ring-green-500',  text: 'text-green-700',   bg: 'bg-green-50',   icon: ShieldCheck },
  mostly_compliant:  { ring: 'ring-emerald-500',text: 'text-emerald-700', bg: 'bg-emerald-50', icon: ShieldCheck },
  needs_work:        { ring: 'ring-amber-500',  text: 'text-amber-700',   bg: 'bg-amber-50',   icon: ShieldAlert },
  non_compliant:     { ring: 'ring-red-500',    text: 'text-red-700',     bg: 'bg-red-50',     icon: ShieldX },
  info:              { ring: 'ring-slate-400',  text: 'text-slate-700',   bg: 'bg-slate-50',   icon: Info },
};

const SEVERITY_BADGE = {
  critical: { label: 'CRITICAL', cls: 'bg-red-100 text-red-700 border-red-200',     icon: XCircle },
  major:    { label: 'MAJOR',    cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  minor:    { label: 'MINOR',    cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Info },
  info:     { label: 'INFO',     cls: 'bg-blue-100 text-blue-700 border-blue-200',   icon: Info },
};

const ScoreBar = ({ score }) => {
  const color = score >= 85 ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="h-2 w-full bg-light-grey rounded-full overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
    </div>
  );
};

const Finding = ({ finding }) => {
  const sev = SEVERITY_BADGE[finding.severity] || SEVERITY_BADGE.info;
  const Icon = sev.icon;
  return (
    <div className="border border-border rounded-lg p-4 bg-background" data-testid={`finding-${finding.severity}`}>
      <div className="flex items-start gap-3 mb-2">
        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-subtle" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className={`text-xs uppercase font-mono ${sev.cls}`}>
              {sev.label}
            </Badge>
            <span className="font-heading font-semibold text-sm text-body">
              {finding.title}
            </span>
          </div>
          {finding.evidence && (
            <p className="text-xs text-subtle mb-2">
              <span className="font-semibold text-body">Evidence:</span> {finding.evidence}
            </p>
          )}
          {finding.rule && (
            <p className="text-xs text-subtle mb-2">
              <span className="font-semibold text-body">Rule:</span> {finding.rule}
            </p>
          )}
          {finding.fix && (
            <p className="text-xs text-jackstone-blue mt-2">
              <span className="font-semibold">Fix:</span> {finding.fix}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryCard = ({ category }) => {
  const v = VERDICT_COLOR[category.verdict] || VERDICT_COLOR.info;
  const Icon = v.icon;
  return (
    <Card className={`p-5 ring-1 ${v.ring}/30`} data-testid={`category-${category.name.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${v.bg}`}>
          <Icon className={`h-5 w-5 ${v.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold text-body">{category.name}</h3>
          <p className="text-xs text-subtle">{category.verdict.replace('_', ' ')}</p>
        </div>
        <div className="text-2xl font-display font-normal text-jackstone-blue">
          {category.score}
          <span className="text-sm text-subtle">/100</span>
        </div>
      </div>
      <ScoreBar score={category.score} />
      {category.findings && category.findings.length > 0 && (
        <div className="mt-4 space-y-2">
          {category.findings.map((f, i) => (
            <Finding key={`${category.name}-${i}`} finding={f} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default function ComplianceChecker() {
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [userNote, setUserNote] = useState('');
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | running | done | error
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setJobId(null);
    setStatus('idle');
    setReport(null);
    setError(null);
    setElapsed(0);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  useEffect(() => () => reset(), []);

  const startAudit = async (e) => {
    e.preventDefault();
    if (!url && !file) {
      toast.error('Provide a URL or upload a file');
      return;
    }
    reset();
    setStatus('running');
    setElapsed(0);

    const fd = new FormData();
    if (url) fd.append('url', url);
    if (file) fd.append('file', file);
    if (userNote) fd.append('user_note', userNote);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/compliance-check`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.job_id) {
        throw new Error(data.detail || data.error || 'Failed to start audit');
      }
      setJobId(data.job_id);
      tickRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const r = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/compliance-check/${data.job_id}`
          );
          const j = await r.json();
          if (j.status === 'done') {
            clearInterval(pollRef.current); pollRef.current = null;
            clearInterval(tickRef.current); tickRef.current = null;
            setReport(j.report);
            setStatus('done');
            toast.success('Audit complete');
          } else if (j.status === 'error') {
            clearInterval(pollRef.current); pollRef.current = null;
            clearInterval(tickRef.current); tickRef.current = null;
            setError(j.error || 'Audit failed');
            setStatus('error');
            toast.error('Audit failed');
          }
        } catch (err) {
          // Transient network errors during polling — keep trying
        }
      }, 2500);
    } catch (err) {
      setStatus('error');
      setError(err.message);
      toast.error(err.message);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error('File is larger than 20 MB. Compress and try again.');
      return;
    }
    setFile(f);
  };

  const verdict = report && VERDICT_COLOR[report.verdict];
  const VerdictIcon = verdict ? verdict.icon : ShieldCheck;

  return (
    <div className="min-h-screen bg-background">
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border"
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-blue.png" alt="Jackstone" className="h-8" data-testid="header-logo" />
            <div className="hidden sm:block h-6 w-px bg-border"></div>
            <div className="hidden sm:block text-sm text-subtle font-medium">
              Brand Compliance Checker
            </div>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2" data-testid="back-to-guidelines-btn">
              <ArrowLeft className="h-4 w-4" />
              Back to Guidelines
            </Button>
          </Link>
        </div>
      </motion.header>

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-3 uppercase">
              Brand Compliance Checker
            </h1>
            <p className="text-lg text-subtle max-w-3xl">
              Paste a URL or upload a PDF, PNG, or JPG. Claude Sonnet 4.6 reads the
              authoritative Jackstone brand rules and returns a structured audit —
              logo, colour palette, typography, voice, imagery, layout.
            </p>
          </motion.div>

          <Card className="p-6 mb-8" data-testid="compliance-form-card">
            <form onSubmit={startAudit} className="space-y-5">
              <div>
                <label className="text-sm font-heading font-semibold text-body mb-2 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-jackstone-blue" />
                  Audit a URL
                </label>
                <Input
                  type="url"
                  placeholder="https://example.com/page-to-audit"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="compliance-url-input"
                  disabled={status === 'running'}
                />
              </div>

              <div className="relative flex items-center gap-3">
                <div className="flex-1 h-px bg-border"></div>
                <span className="text-xs uppercase tracking-wider text-subtle">and / or</span>
                <div className="flex-1 h-px bg-border"></div>
              </div>

              <div>
                <label className="text-sm font-heading font-semibold text-body mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4 text-jackstone-blue" />
                  Upload a file (PDF / PNG / JPG, &lt; 20 MB)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/png,image/jpeg,image/webp"
                    onChange={onFileChange}
                    className="hidden"
                    data-testid="compliance-file-input"
                    disabled={status === 'running'}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={status === 'running'}
                    data-testid="compliance-file-button"
                  >
                    Choose file
                  </Button>
                  <span className="text-sm text-subtle truncate">
                    {file ? `${file.name} (${(file.size / 1024).toFixed(0)} KB)` : 'No file selected'}
                  </span>
                  {file && status !== 'running' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setFile(null)}
                      data-testid="compliance-file-clear"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-heading font-semibold text-body mb-2 block">
                  Reviewer notes (optional)
                </label>
                <Textarea
                  placeholder="e.g. This is a landing page for the HPF-250 plate freezer. Audit logo treatment and headline copy."
                  value={userNote}
                  onChange={(e) => setUserNote(e.target.value)}
                  rows={2}
                  data-testid="compliance-note-input"
                  disabled={status === 'running'}
                />
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  disabled={status === 'running' || (!url && !file)}
                  className="gap-2"
                  data-testid="compliance-submit"
                >
                  {status === 'running' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Auditing… ({elapsed}s)
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" />
                      Run audit
                    </>
                  )}
                </Button>
                {(status === 'done' || status === 'error') && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={reset}
                    data-testid="compliance-reset"
                  >
                    New audit
                  </Button>
                )}
              </div>
            </form>
          </Card>

          {status === 'running' && (
            <Card className="p-6 mb-8 border-jackstone-blue/40 bg-light-blue/30" data-testid="compliance-running-card">
              <div className="flex items-start gap-3">
                <Loader2 className="h-5 w-5 text-jackstone-blue animate-spin mt-0.5" />
                <div>
                  <p className="font-heading font-semibold text-body">
                    Auditing in progress
                  </p>
                  <p className="text-sm text-subtle">
                    Claude Sonnet 4.6 is reading the brand guide and inspecting your content.
                    Typical runtime is 60–90 seconds. Elapsed: <span className="font-mono">{elapsed}s</span>.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {status === 'error' && (
            <Card className="p-6 mb-8 border-red-300 bg-red-50" data-testid="compliance-error-card">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-heading font-semibold text-body">
                    Audit failed
                  </p>
                  <p className="text-sm text-subtle">{error}</p>
                </div>
              </div>
            </Card>
          )}

          {status === 'done' && report && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
              data-testid="compliance-results"
            >
              <Card className={`p-6 ${verdict?.bg || ''} border-2`}>
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${verdict?.bg || ''} ring-2 ${verdict?.ring || ''}`}>
                      <VerdictIcon className={`h-8 w-8 ${verdict?.text || ''}`} />
                    </div>
                    <div>
                      <div className="text-5xl font-display font-normal text-jackstone-blue">
                        {report.overall_score}
                        <span className="text-xl text-subtle">/100</span>
                      </div>
                      <p className={`text-sm font-heading font-semibold uppercase ${verdict?.text || ''}`}>
                        {report.verdict?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-body leading-relaxed">
                      {report.summary}
                    </p>
                  </div>
                </div>
              </Card>

              <div>
                <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
                  Detailed Findings
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {(report.categories || []).map((c) => (
                    <CategoryCard key={c.name} category={c} />
                  ))}
                </div>
              </div>

              {report.strengths && report.strengths.length > 0 && (
                <Card className="p-6 bg-emerald-50 border-emerald-200" data-testid="compliance-strengths">
                  <h2 className="text-xl font-heading font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    What's working well
                  </h2>
                  <ul className="space-y-2">
                    {report.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-body flex gap-2">
                        <span className="text-emerald-600 mt-0.5">•</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {report.next_actions && report.next_actions.length > 0 && (
                <Card className="p-6 bg-amber-50 border-amber-200" data-testid="compliance-next-actions">
                  <h2 className="text-xl font-heading font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Prioritised next actions
                  </h2>
                  <ol className="space-y-2 list-decimal pl-6">
                    {report.next_actions.map((a, i) => (
                      <li key={i} className="text-sm text-body">{a}</li>
                    ))}
                  </ol>
                </Card>
              )}
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
